import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import './EdicaoModal.css';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';

/**
 * O formulário de lançamento vira modal no celular.
 *
 * ── O que acontecia antes ─────────────────────────────────────────────────
 *
 * Tocar no lápis de uma transação chamava `scrollTo('sec-form')` — a página
 * saltava lá para cima e ela tinha que reencontrar onde estava depois de
 * salvar. Num celular a lista de Transações fica no FIM do painel, então o
 * salto atravessava as nove seções inteiras, duas vezes.
 *
 * E o salto é mudo: nada na tela diz "isto aqui em cima agora é a sua
 * transação de ontem". O formulário parece o de sempre, só que preenchido.
 *
 * ── Por que o MESMO formulário, e não um novo ────────────────────────────
 *
 * O formulário de lançamento tem oito campos, parcelamento, categoria nova,
 * débito de reserva e criação de lembrete. Uma segunda cópia "só para editar"
 * seria a armadilha 4 na sua forma mais cara: duas telas que precisam
 * concordar sobre o que é uma transação, e que divergem no primeiro campo
 * novo.
 *
 * Então o elemento é o mesmo — `App.tsx` guarda o `<TransactionForm>` numa
 * variável e escolhe ONDE colocá-lo. O React desmonta e remonta na troca de
 * lugar, o que aqui é o comportamento certo: a remontagem acontece no instante
 * em que a edição começa, que é quando o formulário ia se reinicializar de
 * qualquer jeito.
 *
 * ── Só no celular ────────────────────────────────────────────────────────
 *
 * No computador a seção de lançamento e a lista costumam estar visíveis ao
 * mesmo tempo, e o salto é curto. Um modal ali tiraria da tela o contexto que
 * já estava do lado.
 */

interface Props {
  titulo: string;
  onFechar: () => void;
  /**
   * Onde a página estava quando a edição começou.
   *
   * Não dá para ler aqui: quando este componente monta, o formulário já saiu
   * da seção e o documento já encolheu — o navegador grampeou a rolagem para a
   * nova altura. Quem sabe é quem tocou no lápis.
   */
  voltarPara?: number;
  /**
   * No computador vira caixa no centro; no celular continua a folha que sobe.
   * O de lançamento não usa: ele só abre no celular.
   */
  centralNoDesktop?: boolean;
  children: React.ReactNode;
}

const EdicaoModal: React.FC<Props> = ({ titulo, onFechar, voltarPara, centralNoDesktop, children }) => {
  useTravarScroll(voltarPara);
  /* O voltar do celular fecha isto antes de sair do app. */
  useFechavelComVoltar(true, onFechar);
  const caixa = useRef<HTMLDivElement>(null);
  useFocoNoModal(caixa);

  return createPortal(
    <div className={`edm-overlay${centralNoDesktop ? ' edm-central' : ''}`} onClick={onFechar} ref={caixa}>
      {/*
        O clique de dentro não fecha. Sem isto, escolher uma data no calendário
        ou marcar uma opção fecharia o formulário no meio do preenchimento —
        e o que a pessoa perde é o que ela acabou de digitar.
      */}
      <div className="edm-caixa" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={titulo}>
        <div className="edm-head">
          <span className="edm-titulo">{titulo}</span>
          <button className="edm-fechar" onClick={onFechar} aria-label="Fechar">×</button>
        </div>
        <div className="edm-corpo">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default EdicaoModal;
